import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '@steelbuy/data-access';
import {
    CoatingWeight,
    Compliance,
    Feature,
    getCoatingWeights,
    getCompliances,
    getGradeFamily,
    getListingInputOrderFromRegionCode,
    getMillFinishes,
    getPolishes,
    getProducts,
    getRegion,
    getSurfaceFinishes,
    getSurfaceTreatments,
    GradeFamily,
    MaterialProperties,
    MillFinish,
    Polish,
    Product,
    SurfaceFinish,
    SurfaceTreatment,
} from '@steelbuy/ts-shared';
import {
    ListingSkuInput,
    MaterialAgeSelection,
    MaterialCoatingSelection,
    MaterialFinishSelection,
    MaterialGenericSelection,
    MaterialGradeSelection,
    MaterialOriginSelection,
    MaterialProductDefinitionSelection,
    MaterialProductSelection,
    MaterialProductTypeSelection,
    MaterialQualitySelection,
    MaterialShapeSelection,
    MaterialSpecificationSelection,
    MaterialSurfaceSelection,
    MaterialTemperSelection,
    MaterialTypeSelection,
} from '@steelbuy/ui-domain';
import { MarketingBannerAdvantages, Notification, NotificationLevel } from '@steelbuy/ui-primitive';
import { StepProps } from './StepProps';
import { constants } from '../../../constants';
import { FormLayout } from '../../../views/layouts/form-layout/FormLayout';
import { useCreateListingContext } from '../CreateListingContext';
import { StepVisitation, useCreateListingStep } from '../CreateListingHooks';

import './MaterialStep.scss';

export const MaterialStep = ({ stepId }: StepProps) => {
    const { t } = useTranslation('translation');
    const createListingContext = useCreateListingContext();
    const { materialStepData } = createListingContext;
    const { updateMaterialStepData, updateFileStatus, picturesStatus, certificatesStatus, addFile } =
        createListingContext;
    const { isFeatureSupported } = useFeatureFlag();

    const { formRef, stepVisitation } = useCreateListingStep(stepId);

    const handleNotifyUs = () => {
        const subject = t('application.materialStep.missingProductsEmailSubject');
        window.location.href = `mailto:${t('constants.emailSupport')}?subject=${encodeURIComponent(subject)}`;
    };

    const stepVisited = stepVisitation === StepVisitation.VISITED;

    const availableProducts = getProducts(materialStepData, isFeatureSupported(Feature.PACKAGES));

    let disabledProducts;

    if (createListingContext?.draftId) {
        disabledProducts =
            materialStepData.product === Product.PACKAGE
                ? availableProducts.filter((product) => product !== materialStepData.product)
                : [Product.PACKAGE];
    }

    const componentsMap: Partial<Record<keyof MaterialProperties, ReactElement>> = {
        grade: (
            <MaterialGradeSelection
                material={materialStepData}
                gradeSelected={(grade) => updateMaterialStepData({ grade })}
                required
                forceValidation={stepVisited}
            />
        ),
        temper: (
            <MaterialTemperSelection
                material={materialStepData}
                temperSelected={(temper) => updateMaterialStepData({ temper })}
                required
                forceValidation={stepVisited}
            />
        ),
        specification: (
            <MaterialSpecificationSelection
                material={materialStepData}
                specificationSelected={(specification) => updateMaterialStepData({ specification })}
                required
                forceValidation={stepVisited}
            />
        ),
        surface: (
            <MaterialSurfaceSelection
                material={materialStepData}
                surfaceSelected={(surface) => updateMaterialStepData({ surface })}
                required
                forceValidation={stepVisited}
            />
        ),
        finish: (
            <MaterialFinishSelection
                material={materialStepData}
                finishSelected={(finish) => updateMaterialStepData({ finish })}
                required
                forceValidation={stepVisited}
            />
        ),
        millFinish: (
            <MaterialGenericSelection<MillFinish>
                material={materialStepData}
                onSelected={(millFinish) => updateMaterialStepData({ millFinish })}
                getOptions={getMillFinishes}
                field="millFinish"
                required
                forceValidation={stepVisited}
            />
        ),
        polish: (
            <MaterialGenericSelection<Polish>
                material={materialStepData}
                onSelected={(polish) => updateMaterialStepData({ polish })}
                getOptions={getPolishes}
                field="polish"
                required
                forceValidation={stepVisited}
            />
        ),
        coating: (
            <MaterialCoatingSelection
                material={materialStepData}
                coatingSelected={(coating) => updateMaterialStepData({ coating })}
                coatingThicknessSelected={(coatingThicknessValue) => {
                    updateMaterialStepData({ coatingThicknessValue });
                }}
                coatingColourSelected={(coatingColour) => {
                    updateMaterialStepData({ coatingColour });
                }}
                coatingTypeSelected={(coatingType) => {
                    updateMaterialStepData({ coatingType });
                }}
                coatingCoverageSelected={(coatingCoverage) => {
                    updateMaterialStepData({ coatingCoverage });
                }}
                required
                forceValidation={stepVisited}
            />
        ),
        gradeFamily: (
            <MaterialGenericSelection<GradeFamily>
                material={materialStepData}
                onSelected={(gradeFamily) => updateMaterialStepData({ gradeFamily })}
                getOptions={getGradeFamily}
                field="gradeFamily"
                required
                forceValidation={stepVisited}
            />
        ),
        coatingWeight: (
            <MaterialGenericSelection<CoatingWeight>
                material={materialStepData}
                onSelected={(coatingWeight) => updateMaterialStepData({ coatingWeight })}
                getOptions={getCoatingWeights}
                field="coatingWeight"
                required
                forceValidation={stepVisited}
            />
        ),
        surfaceTreatment: (
            <MaterialGenericSelection<SurfaceTreatment>
                material={materialStepData}
                onSelected={(surfaceTreatment) => updateMaterialStepData({ surfaceTreatment })}
                getOptions={getSurfaceTreatments}
                field="surfaceTreatment"
                required
                forceValidation={stepVisited}
            />
        ),
        compliance: (
            <MaterialGenericSelection<Compliance>
                material={materialStepData}
                onSelected={(compliance) => updateMaterialStepData({ compliance })}
                getOptions={getCompliances}
                field="compliance"
                required
                forceValidation={stepVisited}
            />
        ),
        surfaceFinish: (
            <MaterialGenericSelection<SurfaceFinish>
                material={materialStepData}
                onSelected={(surfaceFinish) => updateMaterialStepData({ surfaceFinish })}
                getOptions={getSurfaceFinishes}
                field="surfaceFinish"
                required
                forceValidation={stepVisited}
            />
        ),
    };

    const listingInputs = getListingInputOrderFromRegionCode(getRegion().regionCode);

    return (
        <div className="material-step">
            <FormLayout>
                <form ref={formRef}>
                    <ListingSkuInput
                        sku={materialStepData.sku}
                        skuSelected={(sku) => updateMaterialStepData({ sku })}
                        required
                        forceValidation={stepVisited}
                    />
                    <MaterialTypeSelection
                        materialType={materialStepData.materialType}
                        materialTypeSelected={(materialType) => updateMaterialStepData({ materialType })}
                        isStainlessFeatureEnabled
                    />
                    <MaterialShapeSelection
                        material={materialStepData}
                        shapeSelected={(shape) => updateMaterialStepData({ shape })}
                    />

                    <div className="material-step__material-product-selection">
                        <MaterialProductSelection
                            material={materialStepData}
                            productSelected={(product) => updateMaterialStepData({ product })}
                            isPackagesSupported={isFeatureSupported(Feature.PACKAGES)}
                            disabledProducts={disabledProducts}
                        />
                        <Notification
                            level={NotificationLevel.INFO}
                            message={t('application.materialStep.missingProductsNotification')}
                            labelActionButton={t('application.materialStep.missingProductsLinkLabel')}
                            onClickActionButton={handleNotifyUs}
                        />
                    </div>

                    <MaterialProductTypeSelection
                        material={materialStepData}
                        typeSelected={(plateType) => updateMaterialStepData({ plateType })}
                        isPrime={materialStepData.prime}
                    />

                    <MaterialProductDefinitionSelection
                        material={materialStepData}
                        definitionSelected={(definition) => updateMaterialStepData({ definition })}
                    />

                    {listingInputs?.map((field) => componentsMap[field])}

                    <MaterialAgeSelection
                        age={materialStepData.age}
                        ageSelected={(age) => updateMaterialStepData({ age })}
                        required
                        forceValidation={stepVisited}
                        material={materialStepData}
                    />

                    <MaterialOriginSelection
                        origins={materialStepData.origins}
                        originsSelected={(origins) => updateMaterialStepData({ origins })}
                        required
                        forceValidation={stepVisited}
                        material={materialStepData}
                    />
                    <div className="material-step__material-quality-selection">
                        <MaterialQualitySelection
                            prime={materialStepData.prime}
                            primeStatusSelected={(prime) => updateMaterialStepData({ prime })}
                            description={materialStepData.description}
                            descriptionChanged={(description) => updateMaterialStepData({ description })}
                            testCertificates={materialStepData.testCertificate}
                            addFile={addFile}
                            pictures={materialStepData.picture}
                            required
                            forceValidation={stepVisited}
                            materialType={materialStepData.materialType}
                            plateType={materialStepData.plateType}
                            multiple={materialStepData.product === Product.PACKAGE}
                            picturesStatus={picturesStatus}
                            certificatesStatus={certificatesStatus}
                            updateFileStatus={updateFileStatus}
                            apiBaseUrl={constants.apiBaseUrl}
                            isCertificateToFollow={materialStepData.certificateToFollow}
                            setIsCertificateToFollow={(isToFollow) => {
                                updateMaterialStepData({
                                    certificateToFollow: isToFollow,
                                    testCertificate: isToFollow ? undefined : materialStepData.testCertificate,
                                });
                            }}
                        />
                        <MarketingBannerAdvantages
                            header={t('application.materialStep.anonymityBannerHeader')}
                            text={t('application.materialStep.anonymityBannerText')}
                        />
                    </div>
                </form>
            </FormLayout>
        </div>
    );
};
